import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Target, TrendingUp, DollarSign, Star } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { JsonLd } from "@/components/JsonLd";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const GoogleAdsMagnoliaPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  const canonicalUrl = generateCanonicalUrl('/google-ads-magnolia');

  const content = {
    es: {
      title: "Google Ads Magnolia TX | Publicidad PPC con Medición orientada a negocio – Luis ROC",
      description: "Campañas de Google Ads en Magnolia, TX gestionadas por un consultor senior: estructura de cuenta, control del gasto y medición. Consulta: (832) 521-5050.",
      h1: "Google Ads en Magnolia TX — Campañas que Convierten",
      intro: "Genera leads y ventas con campañas de Google Ads optimizadas para Magnolia. Segmentación local precisa, seguimiento de conversiones y reporte de coste por lead.",
      ctaButton: "Reunión inicial gratuita de 30 minutos",
      contactButton: "Solicitar Propuesta",
      ctaTitle: "¿Listo para Escalar tu Negocio?",
      breadcrumbs: [
        { name: "Servicios", url: "/servicios" },
        { name: "Localidades", url: "/servicios-locales-houston" },
        { name: "Google Ads Magnolia", url: "/google-ads-magnolia" }
      ]
    },
    en: {
      title: "Google Ads Magnolia TX | PPC Advertising with Business-focused measurement – Luis ROC",
      description: "Google Ads in Magnolia, TX managed by a senior consultant: account structure, spend control and clear measurement. Consultation: (832) 521-5050.",
      h1: "Google Ads in Magnolia TX — Campaigns that Convert",
      intro: "Generate leads and sales with Google Ads campaigns optimized for Magnolia. Precise local targeting, conversion tracking and cost-per-lead reporting.",
      ctaButton: "Free 30-Minute Initial Consultation",
      contactButton: "Request Proposal",
      ctaTitle: "Ready to Scale Your Business?",
      breadcrumbs: [
        { name: "Services", url: "/en/services" },
        { name: "Locations", url: "/servicios-locales-houston" },
        { name: "Google Ads Magnolia", url: "/google-ads-magnolia" }
      ]
    }
  };

  const t = content[language];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Google Ads en Magnolia TX",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": {
      "@type": "City",
      "name": "Magnolia",
      "containedIn": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": t.description
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <meta name="keywords" content="google ads magnolia tx, ppc magnolia texas, publicidad google magnolia, campañas google ads magnolia" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={t.title} />
        <meta property="og:description" content={t.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Magnolia" />
      </Helmet>

      <JsonLd data={serviceSchema} />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main className="container mx-auto px-4 py-8">
          <BreadcrumbNav items={t.breadcrumbs} language={language} />

          {/* Hero Section */}
          <section className="py-16 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              {t.h1}
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              {t.intro}
            </p>

            <Button 
              size="lg" 
              onClick={() => window.location.href = language === 'es' ? '/contacto' : '/en/contact'}
            >
              {t.ctaButton}
            </Button>
          </section>

          {/* Benefits Grid */}
          <section className="py-16">
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <Card className="p-6 text-center">
                <Target className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">
                  {language === 'es' ? 'Segmentación Local' : 'Local Targeting'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'es' ? 'Alcanza clientes en Magnolia' : 'Reach customers in Magnolia'}
                </p>
              </Card>
              
              <Card className="p-6 text-center">
                <DollarSign className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">
                  {language === 'es' ? 'Medición orientada a negocio' : 'Business-focused measurement'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'es' ? 'Tracking completo de conversiones' : 'Complete conversion tracking'}
                </p>
              </Card>
              
              <Card className="p-6 text-center">
                <TrendingUp className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">
                  {language === 'es' ? 'Resultados Rápidos' : 'Fast Results'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'es' ? 'Leads desde día 1' : 'Leads from day 1'}
                </p>
              </Card>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star className="h-6 w-6" fill="currentColor" />
              <Star className="h-6 w-6" fill="currentColor" />
              <Star className="h-6 w-6" fill="currentColor" />
              <Star className="h-6 w-6" fill="currentColor" />
              <Star className="h-6 w-6" fill="currentColor" />
            </div>
            <h2 className="text-3xl font-bold mb-4">{t.ctaTitle}</h2>
            <Button 
              size="lg" 
              variant="secondary" 
              onClick={() => window.location.href = language === 'es' ? '/contacto' : '/en/contact'}
            >
              {t.contactButton}
            </Button>
          </section>
        </main>

        <KnowledgeConnections path="/google-ads-magnolia" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default GoogleAdsMagnoliaPage;
